import React, { useState, useEffect, useCallback } from 'react';
import {
  View, Text, Image, StyleSheet, TouchableOpacity, ScrollView,
  ActivityIndicator, useWindowDimensions,
} from 'react-native';
import {
  ref, listAll, getDownloadURL, getMetadata, deleteObject,
  StorageReference,
} from 'firebase/storage';
import { collection, getDocs } from 'firebase/firestore';
import { db, storage } from '../firebase/firebase';
import { RefreshCw, Trash2, ImageOff, Image as ImageIcon } from 'lucide-react';

const C = {
  primary: '#21808D', primaryLight: '#e8f5f7',
  danger: '#ef4444', dangerLight: '#fee2e2',
  success: '#16a34a', successLight: '#dcfce7',
  bg: '#f6f8fb', card: '#fff',
  textPrimary: '#1a2332', textMuted: '#64748b', border: '#e2e8f0',
};

// Firestore collections that may reference an image stored in Storage.
// A file is "used" if its storage path appears as a download URL in any of
// these collections. Per-collection errors are swallowed so a single missing
// / permission-blocked collection never breaks the whole scan.
const SCAN_COLLECTIONS = [
  'Products', 'products', 'zapee_restaurant_products',
  'zapee_restaurant_partner', 'Restaurant',
  'Categories', 'Category', 'category', 'subcategory', 'CategoryTag',
  'Sellers', 'deliveryPartner', 'zapee_delivery_partners',
  'zapee_share_holders', 'zapee_branches', 'branch',
];

interface PhotoFile {
  fullPath: string;
  name: string;
  url: string;
  used: boolean;
  sizeKB: number | null;
}
interface Folder {
  name: string;
  files: PhotoFile[];   // real photos only (placeholders excluded)
  isEmpty: boolean;     // no real photos — only a folder placeholder keeps it alive
}

// A folder placeholder is a zero-byte object that keeps an otherwise-empty
// folder visible: the Firebase console marker (name "" / trailing "/") or the
// ".keep" dotfiles the Photo Organizer writes when creating a folder.
function isPlaceholder(r: StorageReference): boolean {
  return r.name === '' || r.fullPath.endsWith('/') || r.name.startsWith('.');
}

// Extract the decoded storage object path from a Firebase download URL.
// e.g. https://.../o/products%2Fuid%2F123_a.jpg?alt=media&token=... -> products/uid/123_a.jpg
function pathFromUrl(url: string): string | null {
  if (typeof url !== 'string' || !url.includes('/o/')) return null;
  const m = url.match(/\/o\/([^?]+)/);
  if (!m) return null;
  try { return decodeURIComponent(m[1]); } catch { return m[1]; }
}

// Walk any Firestore value and collect every storage path it references.
function collectUrls(value: any, out: Set<string>) {
  if (value == null) return;
  if (typeof value === 'string') {
    const p = pathFromUrl(value);
    if (p) out.add(p);
  } else if (Array.isArray(value)) {
    value.forEach(v => collectUrls(v, out));
  } else if (typeof value === 'object' && !(value instanceof Date)) {
    Object.values(value).forEach(v => collectUrls(v, out));
  }
}

// Recursively list every file under a storage reference.
async function listAllFiles(folderRef: StorageReference): Promise<StorageReference[]> {
  const res = await listAll(folderRef);
  let files = [...res.items];
  for (const prefix of res.prefixes) {
    files = files.concat(await listAllFiles(prefix));
  }
  return files;
}

export default function PhotosScreen() {
  const { width } = useWindowDimensions();
  const cols = width < 600 ? 2 : width < 1000 ? 3 : width < 1400 ? 4 : 5;

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [activeFolder, setActiveFolder] = useState<string>('all');
  const [filter, setFilter] = useState<'all' | 'used' | 'unused' | 'empty'>('all');
  const [deleting, setDeleting] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Collect every storage path referenced anywhere in Firestore.
      setProgress('Scanning Firestore references…');
      const usedPaths = new Set<string>();
      await Promise.all(SCAN_COLLECTIONS.map(async (col) => {
        try {
          const snap = await getDocs(collection(db, col));
          snap.forEach(d => collectUrls(d.data(), usedPaths));
        } catch {
          /* collection absent or not readable — ignore */
        }
      }));

      // 2. List every file in Storage.
      setProgress('Listing storage files…');
      const refs = await listAllFiles(ref(storage));

      // 3. Resolve url + metadata, classify used/unused, group by top folder.
      //    Track every top-level folder name (including ones kept alive only by
      //    a placeholder) so empty folders still surface for deletion.
      const topOf = (path: string) => (path.includes('/') ? path.split('/')[0] : '(root)');
      const realRefs = refs.filter(r => !isPlaceholder(r));
      const allFolderNames = new Set(refs.map(r => topOf(r.fullPath)));

      setProgress(`Loading ${realRefs.length} photos…`);
      const resolved = await Promise.all(realRefs.map(async (r) => {
        let url = '';
        let sizeKB: number | null = null;
        try { url = await getDownloadURL(r); } catch { /* ignore */ }
        try {
          const meta = await getMetadata(r);
          if (typeof meta.size === 'number') sizeKB = Math.round(meta.size / 1024);
        } catch { /* ignore */ }
        const file: PhotoFile = {
          fullPath: r.fullPath,
          name: r.name,
          url,
          used: usedPaths.has(r.fullPath),
          sizeKB,
        };
        return file;
      }));

      const grouped: Record<string, PhotoFile[]> = {};
      for (const f of resolved) {
        (grouped[topOf(f.fullPath)] ??= []).push(f);
      }
      const folderList: Folder[] = Array.from(allFolderNames)
        .sort()
        .map(name => {
          const files = (grouped[name] ?? []).sort((a, b) => a.fullPath.localeCompare(b.fullPath));
          return { name, files, isEmpty: files.length === 0 };
        });

      setFolders(folderList);
    } catch (e: any) {
      setError(e?.message || 'Failed to load photos');
    } finally {
      setLoading(false);
      setProgress('');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteFile = async (file: PhotoFile) => {
    if (!window.confirm(`Delete this photo permanently?\n\n${file.fullPath}`)) return;
    setDeleting(prev => new Set(prev).add(file.fullPath));
    try {
      await deleteObject(ref(storage, file.fullPath));
      setFolders(prev => prev.map(f => {
        const files = f.files.filter(x => x.fullPath !== file.fullPath);
        return { ...f, files, isEmpty: files.length === 0 };
      }));
    } catch (e: any) {
      window.alert(`Failed to delete: ${e?.message || e}`);
    } finally {
      setDeleting(prev => { const n = new Set(prev); n.delete(file.fullPath); return n; });
    }
  };

  const deleteAllUnused = async (files: PhotoFile[], scopeLabel: string) => {
    const unused = files.filter(f => !f.used);
    if (unused.length === 0) return;
    if (!window.confirm(
      `Delete ALL ${unused.length} unused photo(s) in ${scopeLabel}?\n\nThis cannot be undone.`
    )) return;
    setBulkDeleting(true);
    const failed: string[] = [];
    await Promise.all(unused.map(async (f) => {
      try { await deleteObject(ref(storage, f.fullPath)); }
      catch { failed.push(f.fullPath); }
    }));
    const failedSet = new Set(failed);
    setFolders(prev => prev.map(f => {
      const files = f.files.filter(x => x.used || failedSet.has(x.fullPath));
      return { ...f, files, isEmpty: files.length === 0 };
    }));
    setBulkDeleting(false);
    if (failed.length) window.alert(`${failed.length} photo(s) could not be deleted.`);
  };

  // Delete an empty folder: remove any remaining objects under its prefix
  // (placeholders / leftovers) so the folder fully disappears from Storage.
  const deleteEmptyFolder = async (folderName: string) => {
    if (!window.confirm(`Delete the empty folder "${folderName}"?\n\nThis cannot be undone.`)) return;
    setDeleting(prev => new Set(prev).add(`folder:${folderName}`));
    try {
      if (folderName !== '(root)') {
        const leftovers = await listAllFiles(ref(storage, folderName));
        await Promise.all(leftovers.map(r => deleteObject(r).catch(() => {})));
      }
      setFolders(prev => prev.filter(f => f.name !== folderName));
      if (activeFolder === folderName) setActiveFolder('all');
    } catch (e: any) {
      window.alert(`Failed to delete folder: ${e?.message || e}`);
    } finally {
      setDeleting(prev => { const n = new Set(prev); n.delete(`folder:${folderName}`); return n; });
    }
  };

  // ── Derived view data ──
  const allFiles = folders.flatMap(f => f.files);
  const totalCount = allFiles.length;
  const usedCount = allFiles.filter(f => f.used).length;
  const unusedCount = totalCount - usedCount;
  const emptyFolders = folders.filter(f => f.isEmpty);

  const deleteAllEmptyFolders = async () => {
    if (emptyFolders.length === 0) return;
    if (!window.confirm(
      `Delete ALL ${emptyFolders.length} empty folder(s)?\n\nThis cannot be undone.`
    )) return;
    for (const f of emptyFolders) {
      try {
        if (f.name !== '(root)') {
          const leftovers = await listAllFiles(ref(storage, f.name));
          await Promise.all(leftovers.map(r => deleteObject(r).catch(() => {})));
        }
      } catch { /* ignore individual folder failures */ }
    }
    const emptyNames = new Set(emptyFolders.map(f => f.name));
    setFolders(prev => prev.filter(f => !emptyNames.has(f.name)));
    if (emptyNames.has(activeFolder)) setActiveFolder('all');
  };

  const scopedFolders = activeFolder === 'all'
    ? folders
    : folders.filter(f => f.name === activeFolder);

  const visibleFolders = filter === 'empty'
    // Empty-folder view: only the folders that have no photos.
    ? scopedFolders.filter(f => f.isEmpty)
    : scopedFolders
        .map(f => ({
          ...f,
          files: f.files.filter(x =>
            filter === 'all' ? true : filter === 'used' ? x.used : !x.used
          ),
        }))
        // When filtering by used/unused, hide empty folders (nothing to show).
        .filter(f => filter === 'all' || !f.isEmpty);

  // ── Render ──
  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color={C.primary} />
        <Text style={styles.progressText}>{progress || 'Loading…'}</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={{ paddingBottom: 40 }}>
      {/* Header */}
      <View style={styles.header}>
        <View style={{ flex: 1 }}>
          <Text style={styles.title}>Photos</Text>
          <Text style={styles.subtitle}>
            {totalCount} total · {usedCount} used · {unusedCount} not used
          </Text>
        </View>
        {unusedCount > 0 && (
          <TouchableOpacity
            style={styles.deleteAllBtn}
            disabled={bulkDeleting}
            onPress={() => deleteAllUnused(allFiles, 'the entire bucket')}
          >
            {bulkDeleting
              ? <ActivityIndicator size="small" color="#fff" />
              : <Trash2 size={15} color="#fff" />}
            <Text style={styles.deleteAllTxt}>
              {bulkDeleting ? 'Deleting…' : `Delete all unused (${unusedCount})`}
            </Text>
          </TouchableOpacity>
        )}
        {emptyFolders.length > 0 && (
          <TouchableOpacity style={styles.emptyFoldersBtn} onPress={deleteAllEmptyFolders}>
            <Trash2 size={15} color={C.danger} />
            <Text style={styles.emptyFoldersTxt}>
              Delete empty folders ({emptyFolders.length})
            </Text>
          </TouchableOpacity>
        )}
        <TouchableOpacity style={styles.refreshBtn} onPress={load}>
          <RefreshCw size={16} color="#fff" />
          <Text style={styles.refreshTxt}>Refresh</Text>
        </TouchableOpacity>
      </View>

      {error && (
        <View style={styles.errorBox}><Text style={styles.errorTxt}>{error}</Text></View>
      )}

      {/* Summary stat cards */}
      <View style={styles.statsRow}>
        <View style={[styles.statCard, { borderColor: C.border }]}>
          <Text style={styles.statNum}>{totalCount}</Text>
          <Text style={styles.statLabel}>Total photos</Text>
        </View>
        <View style={[styles.statCard, { borderColor: C.success, backgroundColor: C.successLight }]}>
          <Text style={[styles.statNum, { color: C.success }]}>{usedCount}</Text>
          <Text style={styles.statLabel}>Used</Text>
        </View>
        <View style={[styles.statCard, { borderColor: C.danger, backgroundColor: C.dangerLight }]}>
          <Text style={[styles.statNum, { color: C.danger }]}>{unusedCount}</Text>
          <Text style={styles.statLabel}>Not used</Text>
        </View>
      </View>

      {/* Folder tabs */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.tabsRow}>
        <FolderTab label={`All (${totalCount})`} active={activeFolder === 'all'} onPress={() => setActiveFolder('all')} />
        {folders.map(f => (
          <FolderTab
            key={f.name}
            label={f.isEmpty ? `${f.name} (empty)` : `${f.name} (${f.files.length})`}
            active={activeFolder === f.name}
            onPress={() => setActiveFolder(f.name)}
          />
        ))}
      </ScrollView>

      {/* Used / Unused filter */}
      <View style={styles.filterRow}>
        {(['all', 'used', 'unused', 'empty'] as const).map(key => (
          <TouchableOpacity
            key={key}
            style={[styles.filterChip, filter === key && styles.filterChipActive]}
            onPress={() => setFilter(key)}
          >
            <Text style={[styles.filterChipTxt, filter === key && styles.filterChipTxtActive]}>
              {key === 'all' ? 'All'
                : key === 'used' ? 'Used'
                : key === 'unused' ? 'Not used'
                : `Empty folders (${emptyFolders.length})`}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Folder sections */}
      {visibleFolders.length === 0 && (
        <View style={styles.center}>
          <Text style={styles.emptyTxt}>
            {filter === 'empty' ? 'No empty folders.' : 'No photos to show.'}
          </Text>
        </View>
      )}

      {visibleFolders.map(folder => {
        const folderUnused = folder.files.filter(f => !f.used).length;
        const folderDeleting = deleting.has(`folder:${folder.name}`);
        return (
          <View key={folder.name} style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>{folder.name}</Text>
              <Text style={styles.sectionCount}>
                {folder.isEmpty ? 'empty' : `${folder.files.length} shown`}
              </Text>
              {folder.isEmpty ? (
                <TouchableOpacity
                  style={styles.bulkBtn}
                  disabled={folderDeleting}
                  onPress={() => deleteEmptyFolder(folder.name)}
                >
                  {folderDeleting
                    ? <ActivityIndicator size="small" color={C.danger} />
                    : <Trash2 size={13} color={C.danger} />}
                  <Text style={styles.bulkBtnTxt}>Delete empty folder</Text>
                </TouchableOpacity>
              ) : folderUnused > 0 && (
                <TouchableOpacity
                  style={styles.bulkBtn}
                  disabled={bulkDeleting}
                  onPress={() => deleteAllUnused(folder.files, folder.name)}
                >
                  <Trash2 size={13} color={C.danger} />
                  <Text style={styles.bulkBtnTxt}>Delete {folderUnused} unused</Text>
                </TouchableOpacity>
              )}
            </View>

            {folder.isEmpty ? (
              <View style={styles.emptyFolderBox}>
                <ImageOff size={22} color="#94a3b8" />
                <Text style={styles.emptyFolderTxt}>This folder has no photos.</Text>
              </View>
            ) : (
              <View style={styles.grid}>
                {folder.files.map(file => (
                  <PhotoCard
                    key={file.fullPath}
                    file={file}
                    width={`${100 / cols}%`}
                    deleting={deleting.has(file.fullPath)}
                    onDelete={() => deleteFile(file)}
                  />
                ))}
              </View>
            )}
          </View>
        );
      })}
    </ScrollView>
  );
}

function FolderTab({ label, active, onPress }: { label: string; active: boolean; onPress: () => void }) {
  return (
    <TouchableOpacity style={[styles.tab, active && styles.tabActive]} onPress={onPress}>
      <Text style={[styles.tabTxt, active && styles.tabTxtActive]}>{label}</Text>
    </TouchableOpacity>
  );
}

function PhotoCard({
  file, width, deleting, onDelete,
}: { file: PhotoFile; width: any; deleting: boolean; onDelete: () => void }) {
  return (
    <View style={[styles.cardWrap, { width }]}>
      <View style={styles.card}>
        <View style={styles.thumbWrap}>
          {file.url ? (
            <Image source={{ uri: file.url }} style={styles.thumb} resizeMode="cover" />
          ) : (
            <View style={[styles.thumb, styles.thumbFallback]}>
              <ImageIcon size={28} color="#94a3b8" />
            </View>
          )}
          <View style={[styles.badge, file.used ? styles.badgeUsed : styles.badgeUnused]}>
            {file.used
              ? <ImageIcon size={11} color="#fff" />
              : <ImageOff size={11} color="#fff" />}
            <Text style={styles.badgeTxt}>{file.used ? 'Used' : 'Not used'}</Text>
          </View>
        </View>

        <View style={styles.cardBody}>
          <Text style={styles.fileName} numberOfLines={1}>{file.name}</Text>
          <Text style={styles.filePath} numberOfLines={1}>{file.fullPath}</Text>
          <View style={styles.cardFooter}>
            <Text style={styles.fileSize}>{file.sizeKB != null ? `${file.sizeKB} KB` : '—'}</Text>
            <TouchableOpacity style={styles.delBtn} onPress={onDelete} disabled={deleting}>
              {deleting
                ? <ActivityIndicator size="small" color={C.danger} />
                : <Trash2 size={15} color={C.danger} />}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: C.bg, padding: 16 },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40, minHeight: 200 },
  progressText: { marginTop: 14, color: C.textMuted, fontSize: 14 },
  emptyTxt: { color: C.textMuted, fontSize: 14 },

  header: { flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 16 },
  title: { fontSize: 22, fontWeight: '800', color: C.textPrimary },
  subtitle: { fontSize: 13, color: C.textMuted, marginTop: 2 },
  refreshBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: C.primary, paddingHorizontal: 14, paddingVertical: 9, borderRadius: 8,
  },
  refreshTxt: { color: '#fff', fontWeight: '600', fontSize: 13 },
  deleteAllBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: C.danger, paddingHorizontal: 14, paddingVertical: 9, borderRadius: 8,
  },
  deleteAllTxt: { color: '#fff', fontWeight: '700', fontSize: 13 },
  emptyFoldersBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: C.dangerLight, paddingHorizontal: 14, paddingVertical: 9, borderRadius: 8,
    borderWidth: 1, borderColor: C.danger,
  },
  emptyFoldersTxt: { color: C.danger, fontWeight: '700', fontSize: 13 },
  emptyFolderBox: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    backgroundColor: C.card, borderWidth: 1, borderColor: C.border,
    borderRadius: 12, paddingVertical: 18, paddingHorizontal: 16,
  },
  emptyFolderTxt: { color: C.textMuted, fontSize: 13 },

  errorBox: { backgroundColor: C.dangerLight, borderRadius: 8, padding: 12, marginBottom: 12 },
  errorTxt: { color: C.danger, fontSize: 13 },

  statsRow: { flexDirection: 'row', gap: 12, marginBottom: 16, flexWrap: 'wrap' },
  statCard: {
    minWidth: 120, flexGrow: 1, backgroundColor: C.card, borderWidth: 1.5,
    borderRadius: 12, paddingVertical: 14, paddingHorizontal: 16, alignItems: 'center',
  },
  statNum: { fontSize: 26, fontWeight: '900', color: C.textPrimary },
  statLabel: { fontSize: 12, color: C.textMuted, marginTop: 2, fontWeight: '600' },

  tabsRow: { flexDirection: 'row', marginBottom: 12 },
  tab: {
    paddingHorizontal: 14, paddingVertical: 8, borderRadius: 20, marginRight: 8,
    backgroundColor: C.card, borderWidth: 1, borderColor: C.border,
  },
  tabActive: { backgroundColor: C.primary, borderColor: C.primary },
  tabTxt: { fontSize: 13, color: C.textMuted, fontWeight: '600' },
  tabTxtActive: { color: '#fff' },

  filterRow: { flexDirection: 'row', gap: 8, marginBottom: 18 },
  filterChip: {
    paddingHorizontal: 16, paddingVertical: 7, borderRadius: 8,
    backgroundColor: C.card, borderWidth: 1, borderColor: C.border,
  },
  filterChipActive: { backgroundColor: C.primaryLight, borderColor: C.primary },
  filterChipTxt: { fontSize: 13, color: C.textMuted, fontWeight: '600' },
  filterChipTxtActive: { color: C.primary },

  section: { marginBottom: 24 },
  sectionHeader: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 10 },
  sectionTitle: { fontSize: 16, fontWeight: '700', color: C.textPrimary },
  sectionCount: { fontSize: 12, color: C.textMuted, flex: 1 },
  bulkBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 5,
    backgroundColor: C.dangerLight, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 7,
  },
  bulkBtnTxt: { color: C.danger, fontSize: 12, fontWeight: '700' },

  grid: { flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: -6 },
  cardWrap: { paddingHorizontal: 6, marginBottom: 12 },
  card: {
    backgroundColor: C.card, borderRadius: 12, overflow: 'hidden',
    borderWidth: 1, borderColor: C.border,
  },
  thumbWrap: { position: 'relative', width: '100%', aspectRatio: 1, backgroundColor: '#f1f5f9' },
  thumb: { width: '100%', height: '100%' },
  thumbFallback: { justifyContent: 'center', alignItems: 'center' },
  badge: {
    position: 'absolute', top: 8, left: 8, flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6,
  },
  badgeUsed: { backgroundColor: C.success },
  badgeUnused: { backgroundColor: C.danger },
  badgeTxt: { color: '#fff', fontSize: 11, fontWeight: '700' },

  cardBody: { padding: 10 },
  fileName: { fontSize: 13, fontWeight: '600', color: C.textPrimary },
  filePath: { fontSize: 11, color: C.textMuted, marginTop: 2 },
  cardFooter: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 },
  fileSize: { fontSize: 11, color: C.textMuted },
  delBtn: { padding: 6, borderRadius: 6, backgroundColor: C.dangerLight },
});
